import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  FaCheckCircle,
  FaArrowRight,
  FaLightbulb,
  FaBolt,
  FaChartLine,
  FaShieldAlt,
} from "react-icons/fa";
import "./About.css";
import Banner from "../Banner/Banner";

const About = () => {
  const navigate = useNavigate();

  const values = [
    {
      icon: FaLightbulb,
      title: "Strategic Design & Psychology",
      desc: "Conversion-first architecture crafted to eliminate doubt, build authority, and compel action.",
      gradient: "linear-gradient(135deg, #a855f7 0%, #6366f1 100%)",
    },
    {
      icon: FaBolt,
      title: "Engineering Speed & Performance",
      desc: "Sub-second load times and flawless mobile responsiveness that search engines and users love.",
      gradient: "linear-gradient(135deg, #3b82f6 0%, #06b6d4 100%)",
    },
    {
      icon: FaChartLine,
      title: "Predictable Business Scale",
      desc: "Every design and marketing choice is tied directly to customer acquisition cost and lifetime revenue.",
      gradient: "linear-gradient(135deg, #10b981 0%, #3b82f6 100%)",
    },
  ];

  return (
    <section className="aboutv5">
      <div className="av5-wrap">
        {/* TOP HERO TWO-COLUMN LAYOUT */}
        <div className="av5-split-hero">
          {/* LEFT: VISUAL AGENCY GRAPHIC / ILLUSTRATION */}
          <motion.div
            className="av5-visual-col"
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="av5-graphic-card">
              <div className="av5-card-glow" />

              <div className="av5-graphic-content">
                <div className="av5-badge">
                  <span className="badge-pulse" />
                  <span>Elite Agency Standards</span>
                </div>

                <h3 className="av5-graphic-title">
                  Engineered for Brands that Demand Measurable Outcomes
                </h3>

                <div className="av5-capability-matrix">
                  <div className="matrix-row">
                    <span className="matrix-label">SEO Audit Score</span>
                    <div className="matrix-bar">
                      <motion.div
                        className="matrix-fill"
                        initial={{ width: 0 }}
                        whileInView={{ width: "98%" }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8 }}
                      />
                    </div>
                    <span className="matrix-val">98/100</span>
                  </div>

                  <div className="matrix-row">
                    <span className="matrix-label">Core Web Vitals</span>
                    <div className="matrix-bar">
                      <motion.div
                        className="matrix-fill"
                        initial={{ width: 0 }}
                        whileInView={{ width: "95%" }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.1 }}
                      />
                    </div>
                    <span className="matrix-val">95%</span>
                  </div>

                  <div className="matrix-row">
                    <span className="matrix-label">Lead Conversion Lift</span>
                    <div className="matrix-bar">
                      <motion.div
                        className="matrix-fill"
                        initial={{ width: 0 }}
                        whileInView={{ width: "88%" }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.8, delay: 0.2 }}
                      />
                    </div>
                    <span className="matrix-val">+240%</span>
                  </div>
                </div>

                <div className="av5-floating-tag">
                  <FaShieldAlt className="tag-icon" />
                  <span>100% Bespoke Code · No Cookie-Cutter Templates</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* RIGHT: HEADING, DESCRIPTION, STATISTICS & CTA */}
          <motion.div
            className="av5-content-col"
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <span className="av5-tag">About Our Agency</span>
            <h1 className="av5-title">
              We Build Digital Engines that{" "}
              <span className="text-gradient">Drive Real Business Growth</span>
            </h1>

            <p className="av5-sub">
              We are an agile digital marketing and web engineering team combining
              conversion design, deep search visibility, and paid media execution
              to deliver compounding returns for ambitious brands.
            </p>

            <ul className="av5-feature-list">
              <li>
                <FaCheckCircle className="feat-check" />
                <span>Modern, highly responsive UI engineered to convert</span>
              </li>
              <li>
                <FaCheckCircle className="feat-check" />
                <span>Technical SEO &amp; performance benchmarks baked in</span>
              </li>
              <li>
                <FaCheckCircle className="feat-check" />
                <span>Transparent reporting, weekly updates &amp; zero fluff</span>
              </li>
            </ul>

            {/* STATISTICS ROW */}
            <div className="av5-stats-row">
              <div className="av5-stat-card">
                <div className="stat-k">150+</div>
                <div className="stat-d">Projects Delivered</div>
              </div>
              <div className="av5-stat-card">
                <div className="stat-k">4.9★</div>
                <div className="stat-d">Client Rating</div>
              </div>
              <div className="av5-stat-card">
                <div className="stat-k">&lt;24h</div>
                <div className="stat-d">Response Time</div>
              </div>
            </div>

            {/* CTA BUTTONS */}
            <div className="av5-actions">
              <button
                className="av5-btn av5-btn-primary"
                onClick={() => navigate("/contact")}
              >
                <span>Schedule a Strategy Session</span>
                <FaArrowRight className="btn-arrow" />
              </button>

              <button
                className="av5-btn av5-btn-outline"
                onClick={() => navigate("/services")}
              >
                <span>Explore Services</span>
              </button>
            </div>
          </motion.div>
        </div>

        {/* CORE VALUES SECTION */}
        <section className="av5-values-section">
          <motion.div
            className="av5-values-head"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <span className="av5-tag">Core Principles</span>
            <h2>How We Deliver Competitive Advantage</h2>
          </motion.div>

          <div className="av5-values-grid">
            {values.map((v, idx) => {
              const Icon = v.icon;
              return (
                <motion.div
                  className="av5-val-card"
                  key={v.title}
                  initial={{ opacity: 0, y: 25 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: idx * 0.1 }}
                  whileHover={{ y: -5, transition: { duration: 0.2 } }}
                >
                  <div
                    className="val-icon-box"
                    style={{ background: v.gradient }}
                  >
                    <Icon />
                  </div>
                  <h3>{v.title}</h3>
                  <p>{v.desc}</p>
                </motion.div>
              );
            })}
          </div>
        </section>
      </div>

      <Banner />
    </section>
  );
};

export default About;
